/**
 * <footer-component> — optional footer background parallax and `--footer-height`
 * on `document.documentElement` (ResizeObserver + resize + load).
 *
 * Loaded ONLY from `sections/main-footer.liquid` via
 * `type="module" fetchpriority="low"`.
 *
 * Dependencies:
 *   - @theme/responsive
 */

import { responsive } from '@theme/responsive';

const FOOTER_PARALLAX_BG_SHIFT_CLAMP_PX = 260;
const FOOTER_PARALLAX_BG_FACTOR = -0.32;

class FooterComponent extends HTMLElement {
  #parallaxScrollAttached = false;
  #footerHeightObserver = null;
  #boundOnLoad = () => this.#applyFooterHeightVar();
  #onSectionLoad = () => this.#applyFooterHeightVar();

  #scheduleParallax = () => {
    if (this.scrollFrame) return;
    this.scrollFrame = window.requestAnimationFrame(() => {
      this.scrollFrame = null;
      this.applyParallax();
    });
  };

  constructor() {
    super();
    this.element = this;
    this.footer = null;
    this.initialized = false;
    this.resizeSubscription = null;
    this.scrollFrame = null;
    this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.boundReducedMotionChange = () => {
      this.syncScrollEffect();
    };
    this.parallaxLayer = null;
  }

  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.footer = this.querySelector('.main-footer__wrapper');
    this.parallaxLayer = this.footer?.querySelector('.main-footer__parallax-bg') ?? null;

    this.reducedMotionQuery.addEventListener('change', this.boundReducedMotionChange);

    this.resizeSubscription = responsive.addResizeListener(this, this.syncScrollEffect);

    this.#setupFooterHeightObserver();
    window.addEventListener('load', this.#boundOnLoad, { passive: true });
    document.addEventListener('shopify:section:load', this.#onSectionLoad);

    this.syncScrollEffect();
    requestAnimationFrame(() => this.#applyFooterHeightVar());
  }

  disconnectedCallback() {
    this.initialized = false;

    this.#footerHeightObserver?.disconnect();
    this.#footerHeightObserver = null;
    window.removeEventListener('load', this.#boundOnLoad);
    document.removeEventListener('shopify:section:load', this.#onSectionLoad);
    document.documentElement.style.removeProperty('--footer-height');

    this.reducedMotionQuery.removeEventListener('change', this.boundReducedMotionChange);

    this.#detachParallaxScroll();
    this.parallaxLayer?.style.removeProperty('background-position');
    this.parallaxLayer?.style.removeProperty('transform');
    this.parallaxLayer = null;

    this.resizeSubscription?.removeResizeListener?.();
    this.resizeSubscription = null;

    if (this.scrollFrame) {
      window.cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = null;
    }
  }

  #setupFooterHeightObserver() {
    if (!this.footer) return;
    this.#footerHeightObserver = new ResizeObserver(() => {
      this.#applyFooterHeightVar();
    });
    this.#footerHeightObserver.observe(this.footer);
  }

  #applyFooterHeightVar() {
    if (!this.footer?.isConnected) return;
    document.documentElement.style.setProperty(
      '--footer-height',
      `${Math.round(this.footer.offsetHeight)}px`,
    );
  }

  syncScrollEffect = () => {
    if (!this.footer) return;

    this.#applyFooterHeightVar();

    const shouldParallax =
      Boolean(this.parallaxLayer) && !this.reducedMotionQuery.matches;

    if (!shouldParallax) {
      this.#detachParallaxScroll();
      this.parallaxLayer?.style.removeProperty('background-position');
      this.parallaxLayer?.style.removeProperty('transform');
      return;
    }

    this.#attachParallaxScroll();
    this.#scheduleParallax();
  };

  #attachParallaxScroll() {
    if (this.#parallaxScrollAttached) return;
    window.addEventListener('scroll', this.#scheduleParallax, { passive: true });
    this.#parallaxScrollAttached = true;
  }

  #detachParallaxScroll() {
    if (!this.#parallaxScrollAttached) return;
    window.removeEventListener('scroll', this.#scheduleParallax);
    this.#parallaxScrollAttached = false;
  }

  applyParallax() {
    if (!this.footer || !this.parallaxLayer) return;

    const rect = this.footer.getBoundingClientRect();
    if (rect.height <= 0) return;

    let shiftPx = rect.top * FOOTER_PARALLAX_BG_FACTOR;
    shiftPx = Math.max(
      -FOOTER_PARALLAX_BG_SHIFT_CLAMP_PX,
      Math.min(FOOTER_PARALLAX_BG_SHIFT_CLAMP_PX, shiftPx),
    );
    this.parallaxLayer.style.backgroundPosition = `center calc(50% + ${shiftPx}px)`;
  }
}

if (!customElements.get('footer-component')) {
  customElements.define('footer-component', FooterComponent);
}
